import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';

function Symptoms() {
  const { letter } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [symptomList, setSymptomList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tghNews, setTghNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);

  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  const fetchNews = async () => {
    setNewsLoading(true);
    try {
      const response = await axios.get('/api/news');
      const news = Array.isArray(response.data)
        ? response.data
        : response.data?.news || [];
      news.sort((a, b) => new Date(b.date) - new Date(a.date));
      setTghNews(news);
    } catch (error) {
      console.error('Error fetching news:', error);
      setTghNews([]);
    } finally {
      setNewsLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const searchParam = urlParams.get('search');

    if (searchParam) {
      setSearchQuery(searchParam);
      fetchSymptomsSearch(searchParam);
    } else if (letter) {
      fetchSymptomsByLetter(letter);
    }
  }, [letter, location.search]);

  const fetchSymptomsByLetter = async (ltr) => {
    try {
      setLoading(true);
      setShowSearchResults(false);
      const response = await axios.get(
        `/api/symptoms/from-diseases?letter=${encodeURIComponent(ltr)}`
      );
      setSymptomList(response.data.symptoms || []);
    } catch (error) {
      console.error('Error fetching symptoms:', error);
      setSymptomList([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSymptomsSearch = async (query) => {
    if (!query.trim()) return;
    try {
      setLoading(true);
      setShowSearchResults(true);
      const response = await axios.get(
        `/api/symptoms/from-diseases?search=${encodeURIComponent(query)}`
      );
      setSymptomList(response.data.symptoms || []);
    } catch (error) {
      console.error('Error searching symptoms:', error);
      setSymptomList([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    const first = q.charAt(0).toUpperCase();
    const l = /[A-Z]/.test(first) ? first : '#';
    navigate(`/symptoms/${l}?search=${encodeURIComponent(q)}`);
    window.scrollTo(0, 0);
  };

  const handleLetterClick = (ltr) => {
    navigate(`/symptoms/${ltr}`);
    setShowSearchResults(false);
    setSearchQuery('');
    window.scrollTo(0, 0);
  };

  const groupSymptoms = (list) => {
    const grouped = {};
    list.forEach((s) => {
      const key = s.name.substring(0, 2).toUpperCase();
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(s);
    });
    return grouped;
  };

  const grouped = groupSymptoms(symptomList);

  const symptomLink = (name) =>
    `/symptoms/related?name=${encodeURIComponent(name)}`;

  const newsSidebar = (
    <div className="col-lg-3">
      <div className="border-start ps-4" style={{ borderColor: '#e0e0e0' }}>
        <h6 className="mb-2">TGH News</h6>
        <p className="text-muted small mb-3">
          Latest news and updates from Tema General Hospital.
        </p>
        {newsLoading ? (
          <div className="text-center py-2">
            <div className="spinner-border spinner-border-sm text-primary" role="status" />
            <div className="small text-muted mt-2">Loading news...</div>
          </div>
        ) : tghNews.length === 0 ? (
          <p className="text-muted small mb-0">No news articles available at this time.</p>
        ) : (
          <ul className="list-unstyled small" style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: '0.25rem' }}>
            {tghNews.map((post) => (
              <li key={post.id} className="mb-3 pb-2" style={{ borderBottom: '1px solid #f0f0f0' }}>
                <Link
                  to={`/news/${post.id}`}
                  style={{ color: 'var(--tgh-blue-light)', textDecoration: 'none', fontWeight: 600 }}
                >
                  {post.title}
                </Link>
                {post.date && (
                  <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.15rem' }}>
                    {formatDate(post.date)}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );

  return (
    <>
      <section className="tgh-diseases-hero py-5" style={{ backgroundColor: 'var(--tgh-page-header)', color: 'var(--tgh-on-page-header)' }}>
        <div className="container">
          <nav aria-label="breadcrumb" className="mb-3">
            <ol className="breadcrumb" style={{ backgroundColor: 'transparent', padding: 0 }}>
              <li className="breadcrumb-item"><Link to="/" style={{ color: 'var(--tgh-on-page-header)' }}>Home</Link></li>
              <li className="breadcrumb-item"><Link to="/symptoms" style={{ color: 'var(--tgh-on-page-header)' }}>Symptoms</Link></li>
              {letter && !showSearchResults && (
                <li className="breadcrumb-item active" style={{ color: 'var(--tgh-on-page-header)' }}>Begins with &apos;{letter}&apos;</li>
              )}
              {showSearchResults && (
                <li className="breadcrumb-item active" style={{ color: 'var(--tgh-on-page-header)' }}>Search</li>
              )}
            </ol>
          </nav>

          <h1 className="display-4 mb-3" style={{ fontFamily: 'Lora, serif', fontWeight: 'bold' }}>
            Symptoms
          </h1>
          <p className="lead mb-4" style={{ fontSize: '1.1rem' }}>
            Symptoms listed here come from our diseases and conditions content. Select one to see related articles.
          </p>
          <p className="tgh-diseases-disclaimer mb-4" role="note">
            This is for informational purposes only. For medical advice or a diagnosis, consult a professional or
            healthcare provider.
          </p>

          <div className="row mt-4">
            <div className="col-lg-6">
              <form onSubmit={handleSearch} className="mb-4">
                <div className="input-group input-group-lg">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search symptoms"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ borderRadius: '4px 0 0 4px' }}
                  />
                  <button className="btn btn-light" type="submit" style={{ borderRadius: '0 4px 4px 0' }}>
                    <i className="bi bi-search"></i>
                  </button>
                </div>
              </form>
            </div>
            <div className="col-lg-6">
              <p className="mb-2" style={{ fontSize: '0.9rem', fontWeight: 600 }}>
                Find symptoms by first letter
              </p>
              <div className="d-flex flex-wrap gap-2">
                {alphabet.map((ltr) => (
                  <button
                    key={ltr}
                    type="button"
                    className={`btn ${letter === ltr && !showSearchResults ? 'btn-light' : 'btn-outline-light'}`}
                    onClick={() => handleLetterClick(ltr)}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      fontWeight: '600',
                      border: letter === ltr && !showSearchResults ? '2px solid #fff' : '2px solid rgba(255,255,255,0.5)'
                    }}
                  >
                    {ltr}
                  </button>
                ))}
                <button
                  type="button"
                  className={`btn ${letter === '#' && !showSearchResults ? 'btn-light' : 'btn-outline-light'}`}
                  onClick={() => handleLetterClick('#')}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    fontWeight: '600',
                    border: letter === '#' && !showSearchResults ? '2px solid #fff' : '2px solid rgba(255,255,255,0.5)'
                  }}
                >
                  #
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5" style={{ backgroundColor: '#fff', minHeight: '60vh' }}>
        <div className="container">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : showSearchResults ? (
            <>
              <h2 className="mb-4">Search results for &quot;{searchQuery}&quot;</h2>
              {symptomList.length === 0 ? (
                <p>No symptoms found matching your search.</p>
              ) : (
                <div className="row">
                  <div className="col-lg-9">
                    {Object.keys(grouped).sort().map((key) => (
                      <div key={key} className="mb-5">
                        <h3 className="mb-3" style={{ fontSize: '2rem', fontWeight: 'bold' }}>{key}</h3>
                        <ul className="list-unstyled">
                          {grouped[key].map((s) => (
                            <li key={s.name} className="mb-2">
                              <Link
                                to={symptomLink(s.name)}
                                style={{ color: 'var(--tgh-blue-light)' }}
                                title={s.description || undefined}
                              >
                                {s.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                        <button
                          type="button"
                          className="btn btn-link p-0"
                          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                          style={{ color: 'var(--tgh-blue-light)', fontSize: '0.9rem', textDecoration: 'none' }}
                        >
                          Back to top <i className="bi bi-arrow-up"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                  {newsSidebar}
                </div>
              )}
            </>
          ) : (
            letter && (
              <div className="row">
                <div className="col-lg-9">
                  {symptomList.length === 0 ? (
                    <div className="mb-5">
                      <h3 className="mb-3" style={{ fontSize: '2rem', fontWeight: 'bold' }}>{letter}</h3>
                      <p>No symptoms found starting with &apos;{letter}&apos;. Try another letter or add symptom lists to disease articles in admin.</p>
                    </div>
                  ) : (
                    Object.keys(grouped).sort().map((key) => (
                      <div key={key} className="mb-5">
                        <h3 className="mb-3" style={{ fontSize: '2rem', fontWeight: 'bold' }}>{key}</h3>
                        <ul className="list-unstyled">
                          {grouped[key].map((s) => (
                            <li key={s.name} className="mb-2">
                              <Link
                                to={symptomLink(s.name)}
                                style={{ color: 'var(--tgh-blue-light)' }}
                                title={s.description || undefined}
                              >
                                {s.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                        <button
                          type="button"
                          className="btn btn-link p-0"
                          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                          style={{ color: 'var(--tgh-blue-light)', fontSize: '0.9rem', textDecoration: 'none' }}
                        >
                          Back to top <i className="bi bi-arrow-up"></i>
                        </button>
                      </div>
                    ))
                  )}
                </div>
                {newsSidebar}
              </div>
            )
          )}
        </div>
      </section>
    </>
  );
}

export default Symptoms;
